import type {
  FeatureCompaniesParams,
  FeatureCompaniesQueryInput,
} from '../-types';

/**
 * Columns `buildOrderBy` in
 * `modules/companies/core/db/companies/list-by-platform-feature-id.ts` can
 * actually resolve.
 *
 * Anything else falls through to that query's own default ordering, so sending
 * an unmapped field silently sorts by something the user did not ask for. The
 * column is dropped here instead, leaving the table's arrow off.
 *
 * `updatedAt` is mapped by that query to `cps.updated_at` — when the *link* to
 * this feature last changed, not when the company profile did. That is what the
 * old screen's "Updated At" column meant too.
 */
const SORTABLE_FIELDS = new Set([
  'officialName',
  'name',
  'country',
  'facilityProcesses',
  'updatedAt',
]);

/**
 * Filters the endpoint takes a *list* for.
 *
 * The query shares `buildFilterClause` with the admin companies roster, where
 * `country` is arrayable and `facilityProcesses` is matched by JSON regex — a
 * comma-joined value there means "any of these". Everything else is `useLike`,
 * and `LIKE '%a,b%'` matches nothing, so those stay single-valued.
 */
const MULTI_VALUE_FILTERS = new Set(['country', 'facilityProcesses']);

/**
 * Turns table state into the endpoint's parameters.
 *
 * One conversion point, as on the companies roster: the query key is the object
 * this returns, so the route loader (reading the URL) and the component's hook
 * (reading `useTable`) only share a cache entry when both derive their params
 * the same way.
 *
 * `page` converts from the UI's 1-based count to the endpoint's 0-based one —
 * the query does `OFFSET page * count` with no adjustment of its own.
 */
export function toFeatureCompaniesParams(
  input: FeatureCompaniesQueryInput,
): FeatureCompaniesParams {
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    if (!values || values.length === 0) continue;
    const value = MULTI_VALUE_FILTERS.has(key)
      ? values.join(',')
      : (values[0] as string);
    if (value !== '') filter[key] = value;
  }
  const hasFilter = Object.keys(filter).length > 0;

  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(sort ? { sort } : {}),
    ...(hasFilter ? { filter: JSON.stringify(filter) } : {}),
  };
}

/** Serialises already-converted params. */
export function buildFeatureCompaniesQs(
  params: FeatureCompaniesParams,
): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
